// Язык интерфейса: en, hi, bn, ta, te (иное → en, как на сервере). Строки — из каталога Cricket iOS
// (scripts/import_ios_strings.mjs). Подписи вкладок, статусы, empty_state приходят с сервера переведёнными.
import en from './strings/en.json';
import { telegramUser } from '../telegram/webapp';
import { trackBeforeUnload } from '../analytics/track';

export const LANGUAGES = ['en', 'hi', 'bn', 'ta', 'te'] as const;
export type Lang = (typeof LANGUAGES)[number];
export type StringKey = keyof typeof en;

function normalize(code: string | undefined | null): Lang {
  const base = (code ?? '').toLowerCase().split(/[-_]/)[0];
  return (LANGUAGES as readonly string[]).includes(base ?? '') ? (base as Lang) : 'en';
}

const LANG_KEY = 'cricket.lang';
function storedLang(): string | null { try { return localStorage.getItem(LANG_KEY); } catch { return null; } }

/** Язык: ?lang= (отладка) → выбранный человеком в Настройках → language_code из Telegram → en. */
export const lang: Lang = normalize(new URLSearchParams(location.search).get('lang') ?? storedLang() ?? telegramUser()?.language_code);

/** Названия языков — на самих языках, не переводятся. */
export const LANGUAGE_NAMES: Record<Lang, string> = { en: 'English', hi: 'हिन्दी', bn: 'বাংলা', ta: 'தமிழ்', te: 'తెలుగు' };

/** Сменить язык интерфейса: сохраняем и перезапускаем приложение (словарь и ?lang= всех запросов — на старте).
 *  app_language_change — до перезагрузки: ждём, пока пачка gtag фактически уйдёт в сеть (beacon), не дольше 1 с; не ушла —
 *  событие досылается первым после перезапуска (trackBeforeUnload, HQ-607). Свойство app_language после перезапуска ставит initAnalytics из нового lang. Тот же язык — ничего. */
export function setLanguage(next: Lang): void {
  if (next === lang) return;
  try { localStorage.setItem(LANG_KEY, next); } catch { /* приватный режим — до перезапуска */ }
  void trackBeforeUnload('app_language_change', { language: next }).then(() => location.reload());
}

const fallback: Record<string, string> = en;
let table: Record<string, string> = en;
const loaders = import.meta.glob<Record<string, string>>('./strings/*.json', { import: 'default' });

/** Загрузить словарь текущего языка (отдельный чанк). Вызывается до первого рендера. */
export async function loadStrings(): Promise<void> {
  if (lang === 'en') return;
  try { table = await loaders[`./strings/${lang}.json`]!(); } catch { table = en; }
}

/** Строка интерфейса; {0}, {1}… подставляются по порядку. */
export function t(key: StringKey, ...args: Array<string | number>): string {
  const template = table[key] ?? fallback[key] ?? key;
  return template.replace(/\{(\d+)\}/g, (_, i: string) => String(args[Number(i)] ?? ''));
}

// Даты: всегда григорианский календарь и латинские цифры (урок iOS: системный календарь бывает буддийским,
// а у bn по умолчанию бенгальские цифры).
const locale = `${lang === 'en' ? 'en-IN' : `${lang}-IN`}-u-ca-gregory-nu-latn`;

/** Битая дата (необязательная строка сервера) → '' вместо RangeError, который ронял экран. */
export function formatDate(date: Date, options: Intl.DateTimeFormatOptions): string {
  if (!Number.isFinite(date.getTime())) return '';
  return new Intl.DateTimeFormat(locale, options).format(date);
}

/** Время «HH:MM» по языку приложения (HQ-1397): не через Date без локали — там локаль устройства (bn-BD → «০৩:৩০»). */
export function formatClock(iso: string): string {
  return formatDate(new Date(iso), { hour: '2-digit', minute: '2-digit' });
}
